"use client";

import { useState } from "react";
import Link from "next/link";
import { Logo } from "./Logo";
import { NavLinks, DEFAULT_LINKS, type NavLink } from "./NavLinks";
import { Button } from "./Button";
import { LanguageSwitcher } from "./LanguageSwitcher";
import { MobileMenu } from "./MobileMenu";
import { trackClickStartFree, trackClickHeaderSignin } from "@/lib/metrics";
import { siteConfig } from "@/config/site";
import { getScriptarioHomePath } from "@/config/locales";
import { landingContent } from "@/content/landing";
import { buildAppUrl } from "@/lib/config";
import { appendUtmToUrl } from "@/lib/utm-forward";
import { scrollToAnchor } from "@/lib/scroll-to-anchor";
import { useAccountPresence } from "@/hooks/useAccountPresence";

interface HeaderProps {
  className?: string;
  links?: NavLink[];
  appUrl?: string;
  isLanding?: boolean;
  onNavClick?: (label: string) => void;
  onLogin?: () => void;
  onStartFree?: () => void;
  startFreeLabel?: string;
  compactCtaHref?: string;
  compactCtaAction?: "startFree";
}

function BurgerIcon() {
  return (
    <svg width="20" height="20" viewBox="0 0 20 20" fill="none" aria-hidden="true">
      <path d="M3 5h14M3 10h14M3 15h14" stroke="rgba(0,0,0,0.9)" strokeWidth="1.8" strokeLinecap="round" />
    </svg>
  );
}

export function Header({
  className = "",
  links = DEFAULT_LINKS,
  isLanding,
  onNavClick,
  onLogin,
  onStartFree,
  startFreeLabel,
  compactCtaHref,
  compactCtaAction,
}: HeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const hasAccount = useAccountPresence();

  const handleLogin = onLogin ?? (() => window.open(appendUtmToUrl(buildAppUrl("/login?login=true")), "_self"));
  const handleStartFree = onStartFree ?? (() => window.open(appendUtmToUrl(buildAppUrl("/login?source=try_free&place=header")), "_self"));
  const handleScriptarioStub = () => {
    scrollToAnchor("#pricing");
  };
  const isComingSoon = siteConfig.ctaMode === "coming-soon";

  const doLogin = () => {
    trackClickHeaderSignin();
    if (isComingSoon) handleScriptarioStub();
    else handleLogin();
  };
  const doStartFree = () => {
    trackClickStartFree("header");
    if (isComingSoon) handleScriptarioStub();
    else handleStartFree();
  };
  const doDashboard = () => { window.location.href = appendUtmToUrl(buildAppUrl("/transcriptions")); };
  const scriptarioLocale = siteConfig.locale === "ru" ? "en" : siteConfig.locale;
  const homeHref = siteConfig.siteId === "scriptario" ? getScriptarioHomePath(scriptarioLocale) : "/";
  const hasCompactCta = compactCtaAction === "startFree" || Boolean(compactCtaHref);

  return (
    <>
      <div className="bg-white sticky top-0 z-50 w-full flex justify-center">
      <header
        className={`flex items-center justify-between w-full max-w-[1248px] px-[16px] max-desktop:px-[40px] max-tablet:px-[20px] py-[16px] ${className}`}
        style={{ height: 72 }}
      >
        <Link href={homeHref} className="cursor-pointer shrink-0">
          <Logo className={hasCompactCta ? "max-[430px]:[&>div:last-child]:hidden" : ""} />
        </Link>

        {/* Desktop nav + CTA */}
        <NavLinks links={isLanding ? undefined : links} isLanding={isLanding} onLinkClick={onNavClick} className="hidden desktop:flex" />
        <div className="hidden desktop:flex items-center gap-[16px] shrink-0">
          <LanguageSwitcher />
          {hasAccount ? (
            <Button onClick={doDashboard}>
              {landingContent.header.dashboard}
            </Button>
          ) : (
            <>
              <Button variant="ghost" onClick={doLogin}>
                {landingContent.header.signIn}
              </Button>
              <Button onClick={doStartFree}>
                {startFreeLabel ?? landingContent.header.startFree}
              </Button>
            </>
          )}
        </div>

        {/* Compact action (< desktop) or the default burger trigger. */}
        {compactCtaAction === "startFree" ? (
          <button
            type="button"
            onClick={doStartFree}
            className="desktop:hidden inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-[12px] bg-primary px-[16px] py-[10px] text-[16px] font-medium leading-[20px] text-white transition-[filter] hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary max-tablet:px-[12px] max-tablet:text-[14px]"
          >
            <span className="text-white">Попробовать бесплатно</span>
          </button>
        ) : compactCtaHref ? (
          <a
            href={compactCtaHref}
            onClick={() => trackClickStartFree("header")}
            className="desktop:hidden inline-flex min-h-[40px] shrink-0 items-center justify-center rounded-[12px] bg-primary px-[16px] py-[10px] text-[16px] font-medium leading-[20px] text-white transition-[filter] hover:brightness-110 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary max-tablet:px-[12px] max-tablet:text-[14px]"
          >
            <span className="text-white">Попробовать бесплатно</span>
          </a>
        ) : (
          <button
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label={landingContent.header.openMenu}
            aria-expanded={menuOpen}
            className="desktop:hidden flex items-center justify-center cursor-pointer rounded-[12px] p-[8px] border-none bg-transparent transition-colors hover:bg-black/5"
          >
            <BurgerIcon />
          </button>
        )}
      </header>
      </div>

      <MobileMenu
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        links={isLanding ? undefined : links}
        isLanding={isLanding}
        onNavClick={onNavClick}
        onLogin={doLogin}
        onStartFree={doStartFree}
        startFreeLabel={startFreeLabel}
      />
    </>
  );
}
